import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { Lock, Sparkles, X } from 'lucide-react';
import { usePaywallStore, type PaywallFeature } from '@/store/paywall.store';
import { cn } from '@/lib/cn';

/**
 * Per-feature copy that drives the kicker / title / subtitle. Keep it
 * here — the same modal renders for every gated surface, so the call
 * site only needs to pass a `PaywallFeature` and the right pitch flows
 * automatically.
 */
const FEATURE_COPY: Record<PaywallFeature, { kicker: string; title: string; subtitle: string }> = {
  quiz: {
    kicker: 'Quizzes are part of Ollie Premium',
    title: 'Test what you know. Earn coins.',
    subtitle:
      'Quizzes turn reading into a game — every correct answer earns coins your reader can spend on eggs and creatures. Pick a plan to unlock every quiz on every book and article.',
  },
  'learn-article': {
    kicker: 'Ollie Learns is part of Ollie Premium',
    title: 'Articles and News Safe for Curious Kids.',
    subtitle:
      'Science, history, animals, space, and the way the world works — all written for curious kids by real educators. Pick a plan to unlock every article in the library.',
  },
  'egg-shop': {
    kicker: 'The Egg Shop is part of Ollie Premium',
    title: 'Hatch eggs. Collect creatures.',
    subtitle:
      'Spend coins on eggs, watch them hatch on a timer, and grow a personal menagerie of rare creatures unique to your reader. Pick a plan to start hatching.',
  },
  'learn-with-ollie': {
    kicker: 'Learn With Ollie is part of Ollie Premium',
    title: 'Explore book topics with Ollie.',
    subtitle:
      'Get kid-friendly explanations on every book in the library — questions about characters, themes, history, science, and the curious "why" behind every story. Pick a plan to start asking.',
  },
  'vocabulary-bookmark': {
    kicker: 'Vocabulary bookmarks are part of Ollie Premium',
    title: 'Print-ready bookmarks for every book.',
    subtitle:
      'Each book in the library comes with a hand-curated vocabulary bookmark — printable cards your reader can hold while they read. Pick a plan to download every bookmark.',
  },
  'activity-pdf': {
    kicker: 'Book activities are part of Ollie Premium',
    title: 'Worksheets, coloring pages, and discussion guides.',
    subtitle:
      'Every book ships with a stack of downloadable PDF activities — printable worksheets, coloring pages, and discussion guides hand-curated to extend each story. Pick a plan to download every activity.',
  },
};

const BENEFITS = [
  'Unlimited articles in Ollie Learns',
  'Quizzes, badges, and coin rewards',
  'Egg Shop, hatchery, and creature collection',
  'Learn With Ollie on every book',
];

/**
 * PaywallModal — the single full-screen blocking dialog for every paid
 * feature. Mounted once at the App root; driven by `usePaywallStore`.
 *
 * Visual design intentionally matches the Onboarding wizard chrome
 * (cream → orange gradient header band, soft cards, Fraunces display
 * type) so the upgrade prompt reads as a friendly part of the product
 * rather than a hard sales wall. Single primary CTA → `/pricing`,
 * single ghost "Maybe later" close.
 */
export function PaywallModal() {
  const open = usePaywallStore((s) => s.open);
  const feature = usePaywallStore((s) => s.feature);
  const closePaywall = usePaywallStore((s) => s.closePaywall);
  const navigate = useNavigate();

  const copy = feature ? FEATURE_COPY[feature] : null;

  const handleSeePlans = () => {
    closePaywall();
    navigate('/pricing');
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && closePaywall()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-or-slate-900/65 backdrop-blur-md data-[state=open]:animate-in data-[state=open]:fade-in data-[state=closed]:animate-out data-[state=closed]:fade-out" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-[70] w-full max-w-[560px] -translate-x-1/2 -translate-y-1/2 px-4',
            'data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95',
            'focus:outline-none',
          )}
        >
          <div className="relative overflow-hidden rounded-[28px] border border-white/60 bg-white shadow-or-pop">
            {/* Header band — branded gradient */}
            <div
              className="relative px-7 pb-6 pt-9"
              style={{
                background: 'linear-gradient(135deg, #FFE9D6 0%, #FFF6EC 60%, #FCEDE2 100%)',
              }}
            >
              <Dialog.Close
                className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-or-orange-700/80 transition hover:bg-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40"
                aria-label="Close"
              >
                <X className="h-4 w-4" aria-hidden />
              </Dialog.Close>

              <div className="flex items-start gap-4">
                <div className="relative">
                  <img
                    src="/brand/ollie_logo_square.png"
                    alt=""
                    width={64}
                    height={64}
                    className="rounded-2xl shadow-or-card"
                  />
                  <span
                    aria-hidden
                    className="absolute -bottom-1 -right-1 inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-amber-500 text-white"
                  >
                    <Lock className="h-3 w-3" />
                  </span>
                </div>
                <div className="flex-1">
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-or-orange-200 bg-white/80 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-or-orange-700">
                    <Sparkles className="h-3 w-3" aria-hidden />
                    {copy?.kicker ?? 'Unlock the full Ollie experience'}
                  </div>
                  <Dialog.Title asChild>
                    <h2 className="mt-2 font-display text-[26px] font-bold leading-tight text-or-slate-900">
                      {copy?.title ?? 'Unlock the full Ollie experience'}
                    </h2>
                  </Dialog.Title>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-7 py-6">
              <p className="text-[14px] leading-relaxed text-or-slate-700">{copy?.subtitle}</p>

              <ul className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {BENEFITS.map((b) => (
                  <li
                    key={b}
                    className="inline-flex items-start gap-2 text-[13px] text-or-slate-700"
                  >
                    <span
                      aria-hidden
                      className="mt-[3px] inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-or-orange-100 text-or-orange-700"
                    >
                      <Sparkles className="h-2.5 w-2.5" />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>

              <div className="mt-6 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() => closePaywall()}
                  className="text-[13px] font-semibold text-or-slate-500 hover:text-or-slate-700"
                >
                  Maybe later
                </button>
                <button
                  type="button"
                  onClick={handleSeePlans}
                  className="or-gradient-btn inline-flex h-11 items-center justify-center gap-2 rounded-full px-6 text-[14px] font-semibold"
                >
                  <Sparkles className="h-4 w-4" aria-hidden />
                  See plans &amp; unlock
                </button>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
